// applications/_shared/services/information-request — shared by `espace` and `respond` (see
// `types/information-request.ts`'s own doc comment for why this is shared, not feature-scoped).
import { privateApi } from "@core/libs/axios";
import { parseSafe } from "@core/parse";
import {
  InformationRequestSchema,
  type InformationRequest,
} from "../types/information-request";

/** `GET /applications/{id}/information-requests/` — every request against this dossier, not just
 * OPEN ones (callers filter to what they need). ⚠ Confirmed live: the response is an ENVELOPE,
 * `{"requests": [...]}`, not a bare array — assuming a bare array first silently returned an
 * empty list every time (`Array.isArray` on the envelope object is false), which is exactly why
 * "Action requise" never appeared even against a dossier with real open requests. Parsed leniently
 * per row — one malformed request costs that row, not the whole "Action requise" surface. */
export async function listInformationRequests(
  applicationId: string
): Promise<InformationRequest[]> {
  const dto = await privateApi
    .get<unknown>(`/acquire/applications/${applicationId}/information-requests/`)
    .then((r) => r.data);

  const rows =
    dto && typeof dto === "object" && "requests" in dto && Array.isArray(dto.requests)
      ? dto.requests
      : [];
  return rows
    .map((row) =>
      parseSafe(
        InformationRequestSchema,
        row,
        "applications.shared.listInformationRequests.row"
      )
    )
    .filter((row): row is InformationRequest => row !== null);
}
